import { FootPrintType } from '../../../../graphql/types';
import lookupValues from '../../helpers/lookupValues';

import { FootprintCategoryModel, SubCategory } from './internal';

interface FootprintCategoryComponentModelProps {
  name: string;
  lookupKey: string;
  percentage?: number;
  amount?: number;
  category: FootprintCategoryModel;
  analysisGroup?: string;
  subCategories?: SubCategory[];
}

const getProcessingName = (name: string) => {
  if (name && name.startsWith("Processing")) {
    return "Processing";
  }
  return name;
};

class FootprintCategoryComponentModel {
  name: string;

  readonly lookupKey: string;

  readonly label: string;

  readonly percentage?: number;

  readonly amount?: number;

  readonly category: FootprintCategoryModel;

  readonly subCategories?: SubCategory[];

  analysisGroup: string;

  constructor(data: FootprintCategoryComponentModelProps) {
    this.lookupKey = data.lookupKey;
    this.percentage = data.percentage;
    this.amount = data.amount;
    this.category = data.category;
    this.subCategories = data.subCategories?.map((sc) => ({
      ...sc,
      subCategoryData: FootprintCategoryComponentModel.aggregateSubCategoryData(
        sc.subCategoryData
      ),
    }));
    const analysisGroupsMapping =
      lookupValues.analysisGroupsMapping[
        this.lookupKey as keyof typeof lookupValues.analysisGroupsMapping
      ];

    const name =
      lookupValues.categoryNamingMapping[
        data.name as keyof typeof lookupValues.categoryNamingMapping
      ] || data.name;
    this.name = getProcessingName(name);

    this.label = analysisGroupsMapping
      ? analysisGroupsMapping.label
      : this.name;

    if (data.analysisGroup) this.analysisGroup = data.analysisGroup;
    else if (analysisGroupsMapping)
      this.analysisGroup = analysisGroupsMapping.analysisGroup;
    else if (this.name.match(/from .* to .*/))
      // The format of ingredients is always X, from <origin> to <destination>
      // FIXME: Should we test if this is a feed footprint?
      this.analysisGroup = "Ingredients";
    else if (
      this.name === "Packaging" ||
      this.name === "Electricity for cooling" ||
      this.name.startsWith("Facility") ||
      this.name.startsWith("Processing")
    )
      this.analysisGroup = "Processing";
    else if (this.category.footprint.type === FootPrintType.CompoundFeed)
      this.analysisGroup = "Ingredients";
    else this.analysisGroup = "Unknown";
  }

  /**
   * Returns the direct comparison for this CategoryComponent from the compared FootprintCategory
   * Will return undefined if the category has no compared category or no equal component
   * can be found on the compared category.
   */
  get comparison(): FootprintCategoryComponentModel | undefined {
    if (!this.category.comparison) return undefined;
    const comparedComponent = this.category.comparison.components.find(
      (comparisonComp) => comparisonComp.name === this.name
    );

    // We create a new component based on the old one with a corrected percentage (based on the baseline)
    return (
      comparedComponent &&
      new FootprintCategoryComponentModel({
        ...comparedComponent,
        percentage:
          comparedComponent.amount &&
          (100 * comparedComponent.amount) / this.category.totalAmount,
      })
    );
  }

  /**
   * Returns the comparison percentage of two components
   * Retruns 0 if there is no compared footprint
   */
  get comparisonChangePercentage(): number {
    if (this.comparison?.amount && this.amount) {
      return Math.round((1 - this.comparison.amount / this.amount) * -100);
    }
    return 0;
  }

  setAnalysysGroup(analysisGroup: string) {
    this.analysisGroup = analysisGroup;
  }

  setName(name: string) {
    this.name = name;
  }

  /**
   * Aggregates subCategoryData entries that share the same label by summing
   * their impact and impactAbsolute values. This handles cases where Blonk
   * returns multiple upstream products for the same fertilizer (e.g. Urea
   * production impact + Urea application CO2) that should display as one row.
   * Items that share a label but represent different origins (e.g. "Rice mill
   * feed, AG" vs "Rice mill feed, AR") are NOT aggregated.
   */
  private static aggregateSubCategoryData(
    data: SubCategory['subCategoryData']
  ): SubCategory['subCategoryData'] {
    if (!data || data.length <= 1) return data;
    const grouped = new Map<
      string,
      { name: string; label: string; impact: number; impactAbsolute: number }
    >();
    data.forEach((item) => {
      // Use name as the key to distinguish items with different origins.
      // Items representing sub-processes of the same product (e.g. Urea) will
      // share a label but have completely different names — for those, use label.
      const nameBase = item.name.replace(/,\s*[A-Z]{2,3}$/, '');
      const isOriginVariant = nameBase !== item.name;
      const key = isOriginVariant ? item.name : item.label;

      const existing = grouped.get(key);
      if (existing) {
        existing.impact += item.impact;
        existing.impactAbsolute += item.impactAbsolute;
      } else {
        grouped.set(key, { ...item });
      }
    });
    return Array.from(grouped.values());
  }
}

export default FootprintCategoryComponentModel;
